import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  ListBox as ReactAriaListBox,
  ListBoxProps as ReactAriaListBoxProps,
  useContextProps,
} from 'react-aria-components';
import { mergeClassNames } from '../utils/mergeClassNames';
import styles from './ListBox.module.scss';
import { SelectBaseProps } from './SelectBase';

export type ListBoxProps<T extends object> = ReactAriaListBoxProps<T> & {
  /**
   * @default 'medium'
   */
  size?: SelectBaseProps['size'];
};

export type ListBoxContextValue<T extends object> = ContextValue<
  Omit<ListBoxProps<T>, 'children'>,
  HTMLUListElement
>;

export const ListBoxContext = createContext<ListBoxContextValue<object>>({});

/**
 * A styled ListBox from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `ListBoxContext` with local props (preferring local props).
 */
export const ListBox = <T extends object>(props: ListBoxProps<T>) => {
  const [contextProps] = useContextProps(props, null, ListBoxContext);
  const { className, size = 'medium', ...otherProps } = contextProps;

  return (
    <ReactAriaListBox
      {...otherProps}
      className={(renderProps) =>
        mergeClassNames([styles.listBox, className], renderProps)
      }
      data-size={size}
    />
  );
};
